// src/pages/Users/Techniques/components/Uv2TrainingPanel.jsx

import React, { useMemo, useState } from "react";
import {
  FaBookOpen,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaExternalLinkAlt,
  FaGraduationCap,
  FaImages,
  FaInfoCircle,
  FaRandom,
  FaRedo,
  FaShieldAlt,
} from "react-icons/fa";

import {
  UV2_ANNEX_IMAGES,
  UV2_GRADE_CONFIG,
  UV2_PROGRAM_BY_GRADE,
  UV2_RULES,
} from "../data/uv2ProgramData";

import {
  buildTechniqueLookup,
  countProgramItems,
  generateUv2Draw,
  resolveTechniqueLink,
} from "../utils/uv2TrainingUtils";

export default function Uv2TrainingPanel({
  allTechniques = [],
  onOpenTechnique,
}) {
  const [opened, setOpened] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState("1dan");
  const [draw, setDraw] = useState(null);
  const [showProgram, setShowProgram] = useState(false);
  const [showAnnexes, setShowAnnexes] = useState(false);

  const lookup = useMemo(
    () => buildTechniqueLookup(allTechniques),
    [allTechniques],
  );

  const gradeConfig =
    UV2_GRADE_CONFIG.find((item) => item.key === selectedGrade) ||
    UV2_GRADE_CONFIG[0];

  const rules = UV2_RULES[selectedGrade];
  const program = UV2_PROGRAM_BY_GRADE[selectedGrade];

  const handleGradeChange = (gradeKey) => {
    setSelectedGrade(gradeKey);
    setDraw(null);
  };

  const handleGenerate = () => {
    setDraw(generateUv2Draw(selectedGrade));
  };

  const nageCount = countProgramItems(program?.nage);
  const newazaCount = countProgramItems(program?.newaza);

  return (
    <section className="mt-5 overflow-hidden rounded-[2rem] border border-indigo-100 bg-white shadow-sm dark:border-indigo-950/60 dark:bg-slate-900">
      <button
        type="button"
        onClick={() => setOpened((prev) => !prev)}
        className="flex w-full items-center justify-between gap-4 bg-gradient-to-r from-indigo-950 via-blue-950 to-slate-950 px-5 py-5 text-left text-white sm:px-6"
      >
        <div className="flex min-w-0 items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/15">
            <FaGraduationCap className="text-xl" />
          </div>

          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-indigo-200">
              Passage de grade
            </p>
            <h2 className="mt-1 text-xl font-bold sm:text-2xl">
              Entraînement UV2 Technique/Compétition
            </h2>
            <p className="mt-1 line-clamp-2 text-sm leading-6 text-slate-200">
              Génère un tirage d’entraînement pour réviser le programme UV2
              1er, 2e ou 3e Dan.
            </p>
          </div>
        </div>

        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-white">
          {opened ? <FaChevronUp /> : <FaChevronDown />}
        </div>
      </button>

      {opened && (
        <div className="p-4 sm:p-5">
          <div className="grid gap-3 sm:grid-cols-3">
            {UV2_GRADE_CONFIG.map((grade) => (
              <button
                key={grade.key}
                type="button"
                onClick={() => handleGradeChange(grade.key)}
                className={`rounded-[1.35rem] border px-4 py-3 text-left transition active:scale-[0.98] ${
                  selectedGrade === grade.key
                    ? "border-indigo-300 bg-indigo-50 text-indigo-950 ring-4 ring-indigo-500/10 dark:border-indigo-700 dark:bg-indigo-950/40 dark:text-white"
                    : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900"
                }`}
              >
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                  Grade
                </p>
                <p className="mt-1 text-lg font-bold">{grade.label}</p>
                <p className="mt-1 text-xs font-medium text-slate-500 dark:text-slate-400">
                  {grade.draw.nage} nage-waza · {grade.draw.newaza} ne-waza ·{" "}
                  {grade.draw.defense} défenses
                </p>
              </button>
            ))}
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_360px]">
            <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-200">
                  <FaInfoCircle />
                </div>

                <div>
                  <h3 className="text-lg font-bold text-slate-950 dark:text-white">
                    {rules.title}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                    {rules.intro}
                  </p>

                  <div className="mt-3 rounded-2xl bg-white p-3 text-sm font-bold text-indigo-800 ring-1 ring-indigo-100 dark:bg-slate-900 dark:text-indigo-200 dark:ring-indigo-900/60">
                    {rules.duration}
                  </div>

                  <ul className="mt-3 space-y-2">
                    {rules.points.map((point) => (
                      <li
                        key={point}
                        className="flex gap-2 text-sm leading-6 text-slate-600 dark:text-slate-300"
                      >
                        <FaCheckCircle className="mt-1 shrink-0 text-emerald-500" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">
                Programme
              </p>

              <div className="mt-3 grid grid-cols-2 gap-3">
                <MiniStat label="Nage-waza" value={nageCount} />
                <MiniStat label="Ne-waza" value={newazaCount} />
              </div>

              <div className="mt-3 rounded-2xl bg-slate-50 p-3 dark:bg-slate-900">
                <p className="text-sm font-bold text-slate-900 dark:text-white">
                  Défenses : séries {gradeConfig.defenseSeries.join(" et ")}
                </p>
                <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                  Réponses libres, adaptées, contrôlées et réalistes.
                </p>
              </div>

              <button
                type="button"
                onClick={handleGenerate}
                className="mt-4 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-indigo-700 px-4 text-sm font-bold text-white shadow-lg shadow-indigo-700/15 transition hover:-translate-y-0.5 hover:bg-indigo-800 active:translate-y-0"
              >
                {draw ? <FaRedo /> : <FaRandom />}
                {draw ? "Relancer un tirage" : "Générer un tirage"}
              </button>
            </div>
          </div>

          {selectedGrade !== "1dan" && (
            <CandidateChoiceBlock gradeKey={selectedGrade} />
          )}

          {draw ? (
            <DrawResult
              draw={draw}
              lookup={lookup}
              onOpenTechnique={onOpenTechnique}
            />
          ) : (
            <div className="mt-4 rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-slate-700 dark:bg-slate-950">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-3xl bg-white text-indigo-700 shadow-sm dark:bg-slate-900 dark:text-indigo-200">
                <FaRandom />
              </div>
              <h3 className="mt-3 text-lg font-bold text-slate-950 dark:text-white">
                Aucun tirage généré
              </h3>
              <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
                Choisis ton grade puis lance un tirage d’entraînement. Les
                techniques déjà présentes dans ta bibliothèque pourront être
                ouvertes directement.
              </p>
            </div>
          )}

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <ToggleBlock
              icon={FaBookOpen}
              label="Voir le programme complet"
              opened={showProgram}
              onClick={() => setShowProgram((prev) => !prev)}
            />

            <ToggleBlock
              icon={FaImages}
              label="Voir les annexes JPG"
              opened={showAnnexes}
              onClick={() => setShowAnnexes((prev) => !prev)}
            />
          </div>

          {showProgram && <ProgramDetails program={program} />}

          {showAnnexes && <AnnexesBlock />}
        </div>
      )}
    </section>
  );
}

function CandidateChoiceBlock({ gradeKey }) {
  const isSecondDan = gradeKey === "2dan";

  return (
    <div className="mt-4 rounded-[1.5rem] border border-amber-200 bg-amber-50 p-4 text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-100">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-100">
          <FaShieldAlt />
        </div>
        <div>
          <h3 className="font-bold">
            Partie libre à préparer avant le tirage
          </h3>
          <p className="mt-1 text-sm leading-6">
            {isSecondDan
              ? "Prévoir 1 technique en nage-waza, 1 technique en ne-waza et 1 technique de défense au choix, présentées dans un contexte dynamique."
              : "Prévoir 3 techniques de nage-waza dans 3 directions différentes, 2 techniques de ne-waza en situation de combat et une séquence de défense sur saisies, coups et armes."}
          </p>
        </div>
      </div>
    </div>
  );
}

function DrawResult({ draw, lookup, onOpenTechnique }) {
  return (
    <div className="mt-5 space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-indigo-500">
            Tirage d’entraînement
          </p>
          <h3 className="text-xl font-bold text-slate-950 dark:text-white">
            À démontrer comme en examen
          </h3>
        </div>

        <p className="text-xs font-semibold text-slate-400">
          Tirage généré automatiquement
        </p>
      </div>

      <DrawSection
        title="Nage-waza"
        items={draw.nage}
        lookup={lookup}
        onOpenTechnique={onOpenTechnique}
      />

      <DrawSection
        title="Ne-waza"
        items={draw.newaza}
        lookup={lookup}
        onOpenTechnique={onOpenTechnique}
      />

      <DrawSection
        title="Défenses Ju-jitsu"
        items={draw.defense}
        lookup={lookup}
        onOpenTechnique={onOpenTechnique}
        defense
      />
    </div>
  );
}

function DrawSection({
  title,
  items,
  lookup,
  onOpenTechnique,
  defense = false,
}) {
  return (
    <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
      <h4 className="font-bold text-slate-950 dark:text-white">{title}</h4>

      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item, index) => (
          <DrawItemCard
            key={`${item.id}-${index}`}
            item={item}
            lookup={lookup}
            onOpenTechnique={onOpenTechnique}
            defense={defense}
          />
        ))}
      </div>
    </div>
  );
}

function DrawItemCard({ item, lookup, onOpenTechnique, defense }) {
  const linkedTechnique = resolveTechniqueLink(item.name, lookup);

  return (
    <div className="rounded-[1.25rem] border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
        {item.family}
      </p>

      <h5 className="mt-1 text-base font-bold text-slate-950 dark:text-white">
        {item.name}
      </h5>

      {defense && (
        <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
          Préparer une réponse libre, efficace, contrôlée, sobre et réaliste.
        </p>
      )}

      {linkedTechnique ? (
        <button
          type="button"
          onClick={() => onOpenTechnique?.(linkedTechnique)}
          className="mt-3 inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-3 text-xs font-bold text-white transition active:scale-95 dark:bg-white dark:text-slate-950"
        >
          <FaExternalLinkAlt />
          Voir la fiche
        </button>
      ) : (
        <div className="mt-3 rounded-2xl bg-white px-3 py-2 text-center text-xs font-bold text-slate-400 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800">
          Fiche non reliée
        </div>
      )}
    </div>
  );
}

function ProgramDetails({ program }) {
  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-2">
      <ProgramColumn title="Nage-waza" section={program?.nage} />
      <ProgramColumn title="Ne-waza" section={program?.newaza} />
    </div>
  );
}

function ProgramColumn({ title, section = {} }) {
  return (
    <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
      <h3 className="text-lg font-bold text-slate-950 dark:text-white">
        {title}
      </h3>

      <div className="mt-3 space-y-3">
        {Object.entries(section).map(([family, names]) => (
          <div
            key={family}
            className="rounded-2xl bg-white p-3 ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800"
          >
            <p className="text-sm font-bold text-indigo-700 dark:text-indigo-200">
              {family}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {names.map((name) => (
                <span
                  key={name}
                  className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-200"
                >
                  {name}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnnexesBlock() {
  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-2">
      {UV2_ANNEX_IMAGES.map((image) => (
        <AnnexImage key={image.src} image={image} />
      ))}
    </div>
  );
}

function AnnexImage({ image }) {
  const [hidden, setHidden] = useState(false);

  if (hidden) return null;

  return (
    <div className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
      <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-800">
        <p className="font-bold text-slate-950 dark:text-white">
          {image.title}
        </p>
      </div>

      <img
        src={image.src}
        alt={image.title}
        className="h-auto w-full object-contain"
        loading="lazy"
        onError={() => setHidden(true)}
      />
    </div>
  );
}

function ToggleBlock({ icon: Icon, label, opened, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-12 items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 transition active:scale-[0.98] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200"
    >
      <span className="inline-flex items-center gap-2">
        <Icon />
        {label}
      </span>
      {opened ? <FaChevronUp /> : <FaChevronDown />}
    </button>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-3 text-center ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
      <p className="text-xl font-bold text-slate-950 dark:text-white">
        {value}
      </p>
      <p className="mt-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
        {label}
      </p>
    </div>
  );
}